import * as i0 from '@angular/core';
import { Component, EventEmitter, Input, Output, ContentChildren, ViewChild, HostListener, NgModule, Directive, TemplateRef, ContentChild, forwardRef, Inject, ChangeDetectionStrategy, ViewEncapsulation } from '@angular/core';
import * as i1 from '@angular/common';
import { CommonModule } from '@angular/common';
import { ListKeyManager, SPACE, ENTER, END, HOME } from '@anthem/uxd/util';
import { CarouselTemplateComponent } from '@anthem/uxd/carousel';
import { Subject, defer, merge } from 'rxjs';
import * as i2 from '@angular/cdk/portal';
import { CdkPortal, TemplatePortal, CdkPortalOutlet, PortalModule } from '@angular/cdk/portal';
import { takeUntil, take, switchMap } from 'rxjs/operators';
import * as i3 from '@angular/cdk/a11y';
import { A11yModule } from '@angular/cdk/a11y';

/**
 *  Child class for the purpose of Alphabet Tabs Component transclusion
 *
 *  @param isActive Boolean to set 'active' class on body container
 *  @see AlphabetTabsComponent.ts
 */
class AlphabetTabsBodyComponent {
    /**
     *  Builds the ID necessary for this body content container. This ID
     *  will be primarily used for accessibility -- aria-controls
     */
    buildIdString(id, index) {
        this.resultId = id + '-body-' + index;
    }
    getIsActive() {
        return this.isActive;
    }
    setIsActive(bool) {
        this.isActive = bool;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AlphabetTabsBodyComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.2.12", type: AlphabetTabsBodyComponent, selector: "[data-uxd-alphabet-tabs-body-cmp]", host: { properties: { "class.active": "isActive", "class.hidden": "!isActive", "id": "resultId" } }, ngImport: i0, template: `
      <ng-content></ng-content>
  `, isInline: true }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AlphabetTabsBodyComponent, decorators: [{
            type: Component,
            args: [{
                    selector: '[data-uxd-alphabet-tabs-body-cmp]',
                    template: `
      <ng-content></ng-content>
  `,
                    host: {
                        '[class.active]': 'isActive',
                        '[class.hidden]': '!isActive',
                        '[id]': 'resultId'
                    }
                }]
        }] });
/**
 *  Alphabet Carousel/Ribbon/Tabs global component. Used to index
 *  various content from A - Z; i.e. Glossary or Find Benefits
 *
 *  @param  alphabetItems - Input param to receive alphabet data interface
 *  @param  onAlphabetRangeClick EventEmitter<ICarouselItem> - Emit event for parent container to receive
 *  @see    AlphabetCarouselBodyComponent, IAlphabetCarousel, ICarouselItem
 */
class AlphabetTabsComponent {
    constructor(_renderer) {
        this._renderer = _renderer;
        this.onAlphabetRangeClick = new EventEmitter();
        this._rightMask = true;
    }
    /**
     *  Sets the first content child to be active, if transcluding. If no idPrefix is given in the
     *  data, a default ID will be declared
     */
    ngAfterViewInit() {
        /**
         *  After the view initializes, start listening for scroll
         */
        this._renderer.listen(this.alphabetTabsRef.nativeElement, 'scroll', (scrollEvt) => {
            this._scrollPos = this.alphabetTabsRef.nativeElement.scrollLeft;
            if (this.alphabetTabsRef && this.alphabetTabsRef.nativeElement) {
                this._rightMask = this._scrollPos !== this._maxScrollPos ? true : false;
                this._leftMask = this._scrollPos !== 0 ? true : false;
            }
        });
        if (typeof this.alphabetItems.idPrefix === 'undefined' || this.alphabetItems.idPrefix === '') {
            this.alphabetItems.idPrefix = 'tcp-alphabet-carousel';
        }
        this._isTranscluding = this.alphabetContentChildren.toArray().length > 0;
        if (this._isTranscluding) {
            this._activeAlphaTab = 0;
            this.alphabetContentChildren.toArray()[this._activeAlphaTab].setIsActive(true);
            this.alphabetContentChildren.forEach((alphaBody, $index) => {
                alphaBody.buildIdString(this.alphabetItems.idPrefix, $index);
            });
        }
    }
    /**
     *  Calculate max scroll position after content is finished loading
     */
    ngAfterContentChecked() {
        this.calcMaxScrollPos();
    }
    calcMaxScrollPos() {
        if (this.alphabetTabsRef && this.alphabetTabsRef.nativeElement) {
            this._maxScrollPos = this.alphabetTabsRef.nativeElement.scrollWidth - this.alphabetTabsRef.nativeElement.clientWidth;
        }
        if (this._maxScrollPos === 0) {
            this._rightMask = false;
        }
    }
    onResize(event) {
        this.calcMaxScrollPos();
        let overflow = this._maxScrollPos !== 0;
        this._rightMask = overflow;
    }
    /**
     *  On alphabet range click, assign active index. Emit event; parent listener optional.
     *  If transcluding, will set the corresponding content index to be active
     *  @param  index  - Element index
     *  @param  alphaRange - Carousel item object for event emitter
     *  @see    ICarouselItem
     */
    alphabetRangeClick(index, alphaRange) {
        this._activeAlphaTab = index;
        if (this.alphabetContentChildren.length > 0) {
            this.alphabetContentChildren.forEach((alphaBody) => {
                alphaBody.setIsActive(false);
            });
            this.alphabetContentChildren.toArray()[index].setIsActive(true);
        }
        this.emitRangeClickEvent(alphaRange);
    }
    /**
     *  Emits an event to the parent container of this component
     *  @param  alphaRange ICarouselItem - Carousel item object, contains strings
     *  @see    ICarouselItem
     */
    emitRangeClickEvent(alphaRange) {
        this.onAlphabetRangeClick.emit(alphaRange);
    }
    /**
     *  Builds ID string for aria-controls attribute if transcluding to connect
     *  tab and body for screenreaders
     *  @param Number - Index tagged on at the end of ID
     */
    buildAriaControls(index) {
        let ariaControlsString;
        if (this._isTranscluding) {
            ariaControlsString = this.alphabetItems.idPrefix + '-body-' + index;
        }
        else {
            ariaControlsString = this.alphabetItems.idPrefix + '-body';
        }
        return ariaControlsString;
    }
    /**
     * Reset any active tabs to default state
     */
    resetActiveTab() {
        this._activeAlphaTab = -1;
        if (this.alphabetContentChildren.length > 0) {
            this.alphabetContentChildren.forEach((alphaBody) => {
                alphaBody.setIsActive(false);
            });
        }
    }
    /**
     *  Getters and setters for alphabet tabs component
     */
    get rightMask() {
        return this._rightMask;
    }
    set rightMask(bool) {
        this._rightMask = bool;
    }
    get leftMask() {
        return this._leftMask;
    }
    set leftMask(bool) {
        this._leftMask = bool;
    }
    get maxScrollPos() {
        return this._maxScrollPos;
    }
    get scrollPos() {
        return this._scrollPos;
    }
    get isTranscluding() {
        return this._isTranscluding;
    }
    get activeAlphaTab() {
        return this._activeAlphaTab;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AlphabetTabsComponent, deps: [{ token: i0.Renderer2 }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.2.12", type: AlphabetTabsComponent, selector: "[data-uxd-alphabet-tabs-cmp]", inputs: { alphabetItems: "alphabetItems" }, outputs: { onAlphabetRangeClick: "onAlphabetRangeClick" }, host: { listeners: { "window:resize": "onResize($event)" } }, queries: [{ propertyName: "alphabetContentChildren", predicate: AlphabetTabsBodyComponent }], viewQueries: [{ propertyName: "alphabetTabsRef", first: true, predicate: ["alphabetTabsContainer"], descendants: true }], ngImport: i0, template: `
      <div class="ant-alphabet-tabs-container" [id]="alphabetItems.idPrefix + '-container'">
        <div class="ant-fringe-mask-left" *ngIf="leftMask"></div>
        <div class="ant-fringe-mask" *ngIf="rightMask"></div>
        <div #alphabetTabsContainer class="ant-alphabet-tabs">
          <ul role="tablist" [id]="alphabetItems.idPrefix + '-tablist'">
            <ng-container *ngFor="let alphaRange of alphabetItems.alphabetRanges; let i = index">
              <li tabindex="0" role="tab" [class.active]="activeAlphaTab === i"
              [attr.aria-controls] ="buildAriaControls(i)"
              [attr.aria-selected]="activeAlphaTab === i"
              [id]="alphabetItems.idPrefix + '-item-' + i"
              (click)="alphabetRangeClick(i, alphaRange)"
              (keyup.enter)="alphabetRangeClick(i, alphaRange)">
                <span class="sr-only" [innerText]="'Filter by letter range'"></span>
                <a [innerText]="alphaRange.label" [id]="alphabetItems.idPrefix + '-range-link-' + i"></a>
              </li>
            </ng-container>
          </ul>
        </div>
      </div>
      <div *ngIf="isTranscluding" class="ant-alphabet-tabs-body" [id]="alphabetItems.idPrefix + '-body'">
        <ng-content></ng-content>
      </div>
  `, isInline: true, dependencies: [{ kind: "directive", type: i1.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AlphabetTabsComponent, decorators: [{
            type: Component,
            args: [{
                    selector: '[data-uxd-alphabet-tabs-cmp]',
                    template: `
      <div class="ant-alphabet-tabs-container" [id]="alphabetItems.idPrefix + '-container'">
        <div class="ant-fringe-mask-left" *ngIf="leftMask"></div>
        <div class="ant-fringe-mask" *ngIf="rightMask"></div>
        <div #alphabetTabsContainer class="ant-alphabet-tabs">
          <ul role="tablist" [id]="alphabetItems.idPrefix + '-tablist'">
            <ng-container *ngFor="let alphaRange of alphabetItems.alphabetRanges; let i = index">
              <li tabindex="0" role="tab" [class.active]="activeAlphaTab === i"
              [attr.aria-controls] ="buildAriaControls(i)"
              [attr.aria-selected]="activeAlphaTab === i"
              [id]="alphabetItems.idPrefix + '-item-' + i"
              (click)="alphabetRangeClick(i, alphaRange)"
              (keyup.enter)="alphabetRangeClick(i, alphaRange)">
                <span class="sr-only" [innerText]="'Filter by letter range'"></span>
                <a [innerText]="alphaRange.label" [id]="alphabetItems.idPrefix + '-range-link-' + i"></a>
              </li>
            </ng-container>
          </ul>
        </div>
      </div>
      <div *ngIf="isTranscluding" class="ant-alphabet-tabs-body" [id]="alphabetItems.idPrefix + '-body'">
        <ng-content></ng-content>
      </div>
  `
                }]
        }], ctorParameters: function () { return [{ type: i0.Renderer2 }]; }, propDecorators: { alphabetItems: [{
                type: Input
            }], onAlphabetRangeClick: [{
                type: Output
            }], alphabetContentChildren: [{
                type: ContentChildren,
                args: [AlphabetTabsBodyComponent]
            }], alphabetTabsRef: [{
                type: ViewChild,
                args: ['alphabetTabsContainer']
            }], onResize: [{
                type: HostListener,
                args: ['window:resize', ['$event']]
            }] } });

class ScrollingTabbedViewBodyComponent {
    constructor() {
        this.onClick = new EventEmitter(false);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: ScrollingTabbedViewBodyComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.2.12", type: ScrollingTabbedViewBodyComponent, selector: "[data-uxd-scrolling-tabbed-view-body-cmp]", inputs: { tabData: "tabData" }, outputs: { onClick: "onClick" }, host: { properties: { "class.active": "isActive", "class.hidden": "!isActive", "class.tab-body": "true", "id": "tabData.tabId" } }, ngImport: i0, template: `
      <div [id]="'tab-'+tabData.tabId" role="tabpanel" class="tab-pane" >
        <ng-content>
			</ng-content>
      </div>
  `, isInline: true }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: ScrollingTabbedViewBodyComponent, decorators: [{
            type: Component,
            args: [{
                    selector: '[data-uxd-scrolling-tabbed-view-body-cmp]',
                    template: `
      <div [id]="'tab-'+tabData.tabId" role="tabpanel" class="tab-pane" >
        <ng-content>
			</ng-content>
      </div>
  `,
                    host: {
                        '[class.active]': 'isActive',
                        '[class.hidden]': '!isActive',
                        '[class.tab-body]': 'true',
                        '[id]': 'tabData.tabId'
                    }
                }]
        }], propDecorators: { tabData: [{
                type: Input
            }], onClick: [{
                type: Output
            }] } });

//istanbul ignore next
class ScrollingTabbedViewComponent extends CarouselTemplateComponent {
    constructor() {
        super(...arguments);
        this.onScrollingTabClick = new EventEmitter();
        this.outputObj = {
            index: null,
            functionName: null
        };
        this.activeTab = 0;
    }
    //*ngFor="let tile of scrollingTabbedView.tabs;
    ngAfterViewInit() {
        if (this.tabs.length > 0) {
            this.tabs.toArray()[this.activeTab].isActive = true;
        }
    }
    /** Tranclusion click method */
    tabClick(index, functionName) {
        // tslint:disable-next-line:triple-equals
        if (functionName != undefined) {
            this.outputObj.functionName = functionName;
            this.outputObj.index = index;
            this.emitFunctionName(this.outputObj);
        }
        this.tabs.forEach((t) => {
            t.isActive = false;
        });
        this.activeTab = index;
        this.tabs.toArray()[this.activeTab].isActive = true;
    }
    emitFunctionName(passedOutputObj) {
        this.onScrollingTabClick.emit(passedOutputObj);
    }
    /** Prevent spacebar default scrolling */
    preventSpacebarDefault(event) {
        if (event.keyCode === 32) {
            event.preventDefault();
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: ScrollingTabbedViewComponent, deps: null, target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.2.12", type: ScrollingTabbedViewComponent, selector: "[data-uxd-scrolling-tabbed-view-cmp]", inputs: { scrollingTabbedView: "scrollingTabbedView" }, outputs: { onScrollingTabClick: "onScrollingTabClick" }, queries: [{ propertyName: "tabs", predicate: ScrollingTabbedViewBodyComponent }], viewQueries: [{ propertyName: "ulWrapper", first: true, predicate: ["ulWrapper"], descendants: true }], usesInheritance: true, ngImport: i0, template: `
  <div *ngIf="tabs.length !== 0">

<div class="carousel-selector-cmp" >
<div #LeftControl id="tcp-covcrsl-left-ctrl" class="carousel-left-control" (click)="scroll('left')">
    <button aria-label="Move carousel left" [class.disabled]="disableLeft" class="fa fa-chevron-left"></button>
</div>
<div #ViewPort class="carousel-content-container">

  <ul #CarouselList>
    <li #CarouselItem *ngFor="let tile of scrollingTabbedView.tabs; let i = index;" [style.left.%]="scrollPos" (click)="tabClick(i,tile.functionName)" >
      <!-- coverage -->
      <div [id]="'tcp-covcrsl-cov-prd-'+i" class="tcp-coverage-item" (click)="selectedIndex=i" [ngClass]="{'active': i==selectedIndex }">
        <div class="period-selector-container text-center">
          <div class="ant-font-sb">
            <span class="scrolling-tab-title" innerHTML="{{tile.heading}}"></span>
          </div>
            <span class="scrolling-tab-description" innerHTML="{{tile.subHeading}}"></span>
        </div>
      </div>
      <!-- end of coverage -->
    </li>
  </ul>

</div>
  <div #RightControl id="tcp-covcrsl-right-ctrl" class="carousel-right-control" (click)="scroll('right')">
      <button aria-label="Move carousel right" [class.disabled]="disableRight" class="fa fa-chevron-right"></button>
  </div>
  <div class="tab-content">
  <ng-content>
  </ng-content>
  </div>

</div>
`, isInline: true, dependencies: [{ kind: "directive", type: i1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i1.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: ScrollingTabbedViewComponent, decorators: [{
            type: Component,
            args: [{
                    selector: "[data-uxd-scrolling-tabbed-view-cmp]",
                    template: `
  <div *ngIf="tabs.length !== 0">

<div class="carousel-selector-cmp" >
<div #LeftControl id="tcp-covcrsl-left-ctrl" class="carousel-left-control" (click)="scroll('left')">
    <button aria-label="Move carousel left" [class.disabled]="disableLeft" class="fa fa-chevron-left"></button>
</div>
<div #ViewPort class="carousel-content-container">

  <ul #CarouselList>
    <li #CarouselItem *ngFor="let tile of scrollingTabbedView.tabs; let i = index;" [style.left.%]="scrollPos" (click)="tabClick(i,tile.functionName)" >
      <!-- coverage -->
      <div [id]="'tcp-covcrsl-cov-prd-'+i" class="tcp-coverage-item" (click)="selectedIndex=i" [ngClass]="{'active': i==selectedIndex }">
        <div class="period-selector-container text-center">
          <div class="ant-font-sb">
            <span class="scrolling-tab-title" innerHTML="{{tile.heading}}"></span>
          </div>
            <span class="scrolling-tab-description" innerHTML="{{tile.subHeading}}"></span>
        </div>
      </div>
      <!-- end of coverage -->
    </li>
  </ul>

</div>
  <div #RightControl id="tcp-covcrsl-right-ctrl" class="carousel-right-control" (click)="scroll('right')">
      <button aria-label="Move carousel right" [class.disabled]="disableRight" class="fa fa-chevron-right"></button>
  </div>
  <div class="tab-content">
  <ng-content>
  </ng-content>
  </div>

</div>
`
                }]
        }], propDecorators: { scrollingTabbedView: [{
                type: Input
            }], onScrollingTabClick: [{
                type: Output
            }], tabs: [{
                type: ContentChildren,
                args: [ScrollingTabbedViewBodyComponent]
            }], ulWrapper: [{
                type: ViewChild,
                args: ["ulWrapper"]
            }] } });

/**
 *  Child component class that contains the body for transcluded tiles
 *  @param  tabData Pass an object containing header label and ID for tile
 *  @see    tabbedTilesCmp.ts
 */
class TabbedTileComponent {
    constructor() {
        this.isActive = false;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: TabbedTileComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.2.12", type: TabbedTileComponent, selector: "[data-uxd-tabbed-tile-cmp]", inputs: { tabData: "tabData" }, host: { properties: { "class.inactive": "!isActive", "class.active": "isActive", "class.tab-body": "true", "id": "tabData.tabId" } }, ngImport: i0, template: `
          <h5>
            <span [innerHTML]="tabData.label"></span>
          </h5>
          <div>
            <ng-content>
            </ng-content>
          </div>
  `, isInline: true }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: TabbedTileComponent, decorators: [{
            type: Component,
            args: [{
                    selector: '[data-uxd-tabbed-tile-cmp]',
                    template: `
          <h5>
            <span [innerHTML]="tabData.label"></span>
          </h5>
          <div>
            <ng-content>
            </ng-content>
          </div>
  `,
                    host: {
                        '[class.inactive]': '!isActive',
                        '[class.active]': 'isActive',
                        '[class.tab-body]': 'true',
                        '[id]': 'tabData.tabId'
                    }
                }]
        }], propDecorators: { tabData: [{
                type: Input
            }] } });

/**
 *  Colored tab tiles component
 *  @param tilesContent  - Array of tile data
 *  @param tiles  - containers list of child Tile components to access/reference
 *  @param activeTab - variable to hold the currently selected tab in the view
 *  @see ITabTiles, TabbedTileComponent
 */
class TabbedTilesComponent {
    constructor() {
        this.onTabClick = new EventEmitter();
        this.activeTab = 0;
    }
    /**
     *  After content is initialized, set first tile to active on page load
     */
    ngAfterContentInit() {
        if (this.tilesContent.default) {
            this.activeTab = this.tilesContent.default;
        }
        if (this.tiles.length > 0) {
            if (this.tilesContent.default) {
                this.resetActiveTile(this.tilesContent.default);
            }
            else {
                this.resetActiveTile(0);
            }
        }
    }
    /**
     *  After the content loads, set first tile to active if no current
     *  child tile is set to active; handles reload from dropdown
     */
    ngAfterContentChecked() {
        let activeCount = 0;
        if (this.tiles.length > 0) {
            this.tiles.forEach((t, index) => {
                if (t.isActive === true) {
                    activeCount = index;
                }
            });
            this.resetActiveTile(activeCount);
        }
    }
    /**
     *  Sets the isActive boolean of passed in index to true
     *  @param number - index to search for inside QueryList array
     */
    resetActiveTile(index) {
        this.tiles.toArray()[index].isActive = true;
    }
    /**
     *  On tile click, set the activeTab to passed in Index
     *  @param index number - index passed in from user click
     */
    tabClick(index) {
        if (this.tiles.length > 0) {
            this.tiles.forEach((t) => {
                t.isActive = false;
            });
            this.tiles.toArray()[index].isActive = true;
        }
        this.activeTab = index;
        //EMIT index to any listeners
        this.onTabClick.emit(index);
    }
    /**
     * hasTheme checks whether the color property has been defined on interface
     * @param tilesContent
     */
    hasTheme(tilesContent) {
        if (!tilesContent.color && tilesContent.color !== '') {
            return false;
        }
        return true;
    }
    /**
     * themeIsBlue returns true if color is set to blue on interface
     * @param tilesContent
     */
    themeIsBlue(tilesContent) {
        if (!this.hasTheme(tilesContent)) {
            return false;
        }
        let color = tilesContent.color.toLowerCase();
        if (color === 'blue') {
            return 'blue';
        }
        return false;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: TabbedTilesComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.2.12", type: TabbedTilesComponent, selector: "[data-uxd-tabbed-tiles-cmp]", inputs: { tilesContent: "tilesContent" }, outputs: { onTabClick: "onTabClick" }, host: { properties: { "class.tab-wrapper": "true" } }, queries: [{ propertyName: "tiles", predicate: TabbedTileComponent }], ngImport: i0, template: `
    <!-- If transcluding content (Data is put in the view manually) -->
    <div *ngIf="tiles.length !== 0">
      <ul role="tablist" class="clearfix" [ngClass]="{'ant-blue-tabs': themeIsBlue(tilesContent)}">
        <ng-container *ngFor="let tile of tiles; let first = first; let $index = index">
          <li class="tab-tile" [id]="tilesContent.id + $index" role="tab" (click)="tabClick($index)"
          (keyup.enter)="tabClick($index)" (keyup.space)="tabClick($index)" tabindex="0"
          [attr.aria-controls]="tile.tabData.tabId" [class.active]="tile.isActive" [attr.aria-selected]="tile.isActive">
            <h6 [attr.data-analytics]="tile.tabData.analyticsText">
              <span class="tab-tile-label" [innerText]="tile.tabData.label" [attr.data-analytics]="tile.tabData.analyticsText"></span>
              <span class="fa" [ngClass]="tile.tabData.icon" [attr.aria-label]="tile.tabData.label" [attr.data-analytics]="tile.tabData.analyticsText"></span>
            </h6>
          </li>
        </ng-container>
      </ul>
      <!-- Transclusion goes in here -->
      <ng-content>
      </ng-content>
      <!-- End transclusion -->
    </div>

    <!-- Non transclusion (Defaults to data from JSON object) -->
    <ng-container *ngIf="tiles.length === 0">
      <ul role="tablist" class="clearfix" [ngClass]="{'ant-blue-tabs': themeIsBlue(tilesContent)}">
        <li class="tab-tile" [id]="tilesContent.id + $index" role="tab" *ngFor="let tile of tilesContent.tabs; let first = first; let $index = index"
        (click)="tabClick($index)" (keyup.enter)="tabClick($index)"
        (keyup.space)="tabClick($index)" tabindex="0"
        [attr.aria-controls]="'tabTile'+$index" [class.active]="activeTab === $index"
        [attr.aria-selected]="activeTab === $index">
          <h6 [attr.data-analytics]="tile.analyticsText">
            <span class="tab-tile-label" [innerText]="tile.label" [attr.data-analytics]="tile.analyticsText"></span>
            <span class="fa" [ngClass]="tile.icon" [attr.aria-label]="tile.label" [attr.data-analytics]="tile.analyticsText"></span>
          </h6>
        </li>
      </ul>
      <div class="tab-body" *ngFor="let tile of tilesContent.tabs; let first = first; let $index = index"
      (click)="tabClick($index)" [id]="'tabTile'+$index" [class.active]="activeTab === $index" [class.inactive]="activeTab != $index">
        <h5 [innerHTML]="tile.label"></h5>
        <div [innerHTML]="tile.content"></div>
      </div>
    </ng-container>
  `, isInline: true, dependencies: [{ kind: "directive", type: i1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i1.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: TabbedTilesComponent, decorators: [{
            type: Component,
            args: [{
                    selector: '[data-uxd-tabbed-tiles-cmp]',
                    host: { '[class.tab-wrapper]': 'true' },
                    template: `
    <!-- If transcluding content (Data is put in the view manually) -->
    <div *ngIf="tiles.length !== 0">
      <ul role="tablist" class="clearfix" [ngClass]="{'ant-blue-tabs': themeIsBlue(tilesContent)}">
        <ng-container *ngFor="let tile of tiles; let first = first; let $index = index">
          <li class="tab-tile" [id]="tilesContent.id + $index" role="tab" (click)="tabClick($index)"
          (keyup.enter)="tabClick($index)" (keyup.space)="tabClick($index)" tabindex="0"
          [attr.aria-controls]="tile.tabData.tabId" [class.active]="tile.isActive" [attr.aria-selected]="tile.isActive">
            <h6 [attr.data-analytics]="tile.tabData.analyticsText">
              <span class="tab-tile-label" [innerText]="tile.tabData.label" [attr.data-analytics]="tile.tabData.analyticsText"></span>
              <span class="fa" [ngClass]="tile.tabData.icon" [attr.aria-label]="tile.tabData.label" [attr.data-analytics]="tile.tabData.analyticsText"></span>
            </h6>
          </li>
        </ng-container>
      </ul>
      <!-- Transclusion goes in here -->
      <ng-content>
      </ng-content>
      <!-- End transclusion -->
    </div>

    <!-- Non transclusion (Defaults to data from JSON object) -->
    <ng-container *ngIf="tiles.length === 0">
      <ul role="tablist" class="clearfix" [ngClass]="{'ant-blue-tabs': themeIsBlue(tilesContent)}">
        <li class="tab-tile" [id]="tilesContent.id + $index" role="tab" *ngFor="let tile of tilesContent.tabs; let first = first; let $index = index"
        (click)="tabClick($index)" (keyup.enter)="tabClick($index)"
        (keyup.space)="tabClick($index)" tabindex="0"
        [attr.aria-controls]="'tabTile'+$index" [class.active]="activeTab === $index"
        [attr.aria-selected]="activeTab === $index">
          <h6 [attr.data-analytics]="tile.analyticsText">
            <span class="tab-tile-label" [innerText]="tile.label" [attr.data-analytics]="tile.analyticsText"></span>
            <span class="fa" [ngClass]="tile.icon" [attr.aria-label]="tile.label" [attr.data-analytics]="tile.analyticsText"></span>
          </h6>
        </li>
      </ul>
      <div class="tab-body" *ngFor="let tile of tilesContent.tabs; let first = first; let $index = index"
      (click)="tabClick($index)" [id]="'tabTile'+$index" [class.active]="activeTab === $index" [class.inactive]="activeTab != $index">
        <h5 [innerHTML]="tile.label"></h5>
        <div [innerHTML]="tile.content"></div>
      </div>
    </ng-container>
  `
                }]
        }], propDecorators: { tilesContent: [{
                type: Input
            }], onTabClick: [{
                type: Output
            }], tiles: [{
                type: ContentChildren,
                args: [TabbedTileComponent]
            }] } });

class TabbedViewBodyComponent {
    constructor() {
        this.onClick = new EventEmitter(false);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: TabbedViewBodyComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.2.12", type: TabbedViewBodyComponent, selector: "[data-uxd-tabbed-body-cmp]", inputs: { tabData: "tabData" }, outputs: { onClick: "onClick" }, host: { properties: { "class.hidden": "!tabData.isActive", "class.active": "tabData.isActive", "class.tab-body": "true", "id": "tabData.tabId" } }, ngImport: i0, template: `
      <div [id]="'tab-'+tabData.tabId" role="tabpanel" class="tab-pane" >
        <ng-content>
			</ng-content>
      </div>
  `, isInline: true }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: TabbedViewBodyComponent, decorators: [{
            type: Component,
            args: [{
                    selector: '[data-uxd-tabbed-body-cmp]',
                    template: `
      <div [id]="'tab-'+tabData.tabId" role="tabpanel" class="tab-pane" >
        <ng-content>
			</ng-content>
      </div>
  `,
                    host: {
                        '[class.hidden]': '!tabData.isActive',
                        '[class.active]': 'tabData.isActive',
                        '[class.tab-body]': 'true',
                        '[id]': 'tabData.tabId'
                    }
                }]
        }], propDecorators: { tabData: [{
                type: Input
            }], onClick: [{
                type: Output
            }] } });

//istanbul ignore next
class TabbedViewComponent {
    constructor() {
        this.tabbedView = {};
        /**
         * @property _activeTabIndex
         * The index of the currently active TabbedViewBodyComponent
         */
        this._activeTabIndex = null;
    }
    set activeTab(index) {
        this._activeTabIndex = index > -1 ? index : 0;
        this._activateTab(this._activeTabIndex);
    }
    get activeTab() {
        return this._activeTabIndex;
    }
    /**
     * @param activeIndex The index of the TabbedViewBodyComponent that should
     * be made active.
     */
    _activateTab(activeIndex) {
        let activeTabTile = null;
        let activeTabContent = null;
        /* loop through all TabbedViewBodyComponents in the QueryList, and set 'activeTabContent'
        * to to the tab at the currently active index.  */
        if (this.tabs) {
            this.tabs.forEach((tab, tabIndex) => {
                /* Reset all tabs to in-active to prevent multiple tabs from being opened at once. */
                tab.tabData.isActive = false;
                if (tabIndex === activeIndex) {
                    activeTabContent = tab;
                }
            });
        }
        /* Reflect the change to 'activeTabIndex' in the 'tabbedView' JSON, which
         * will update the HTML and activate the correct tab. */
        if (this.tabbedView && this.tabbedView.tabs) {
            activeTabTile = this.tabbedView.tabs[activeIndex];
            if (activeTabTile) {
                activeTabTile.isActive = true;
            }
        }
        /* Activate the TabbedViewBodyComponent found above, if it exists.
         * This activates the actual content of the tab.*/
        if (activeTabContent && activeTabContent.tabData) {
            activeTabContent.tabData.isActive = true;
        }
        /* Finally, update the '_activeTabIndex' property */
        this._activeTabIndex = activeIndex;
    }
    /**
     * @param tile The interface object from which to grab the data-analytics ID.
     * @returns the data-analytics string unique to the element or
     * component. If there is no data-analytics ID, return null and do not populate the
     * data-analytics attribute on the HTML element.
     */
    getDataAnalytics(tile) {
        if (tile && tile.analyticsText) {
            return tile.analyticsText;
        }
        return null;
    }
    ngOnInit() {
        if (!this.tabbedView.color) {
            this.tabbedView.color = 'blue';
        }
        /* On first load of the component, loop through the JSON to find if
         * any of the tabs should be active by default */
        if (this.tabbedView && this.tabbedView.tabs) {
            this.tabbedView.tabs.forEach((tab, index) => {
                if (tab.isActive === true) {
                    this._activateTab(index);
                    return;
                }
            });
        }
    }
    /* Keyboard event listener for tab controls */
    onKeyPress(index, event) {
        /* If the key pressed was either the spacebar (32) or the enter key (13),
         * set the active tab to the index passed. */
        if (event.keyCode === 32 || event.keyCode === 13) {
            this.preventSpacebarDefault(event);
            this.activeTab = index;
        }
    }
    /** Non-transclusion click method */
    tileClick(index, event) {
        this.preventSpacebarDefault(event);
        this.activeTab = index;
    }
    /** Prevent spacebar default scrolling */
    preventSpacebarDefault(event) {
        if (event.keyCode === 32) {
            event.preventDefault();
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: TabbedViewComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.2.12", type: TabbedViewComponent, selector: "[data-uxd-tabbed-view-cmp], [data-uxd-tabbed-view-v2-cmp]", inputs: { tabbedView: "tabbedView", activeTab: "activeTab" }, queries: [{ propertyName: "tabs", predicate: TabbedViewBodyComponent, descendants: true }], ngImport: i0, template: `
    <div class="tabs" [ngClass]="tabbedView.color">
      <ul class="nav nav-pills nav-justified" role='tablist'>
        <li  role="presentation"
            [class.active]="activeTab === $index"
            *ngFor="let tile of tabbedView.tabs; let first = first; let $index = index">
          <a (click)="tileClick($index, $event)"
            (keydown)="onKeyPress($index, $event)"
             href="javascript:void(0)"
            class="viewButton"
            [attr.aria-controls]="tile.tabId" role="tab"
            data-toggle="tab"
            [attr.aria-selected]="activeTab === $index"
            [attr.data-analytics]="getDataAnalytics(tile)"
            [innerHTML]="tile.heading" tabindex="0"></a>
        </li>
      </ul>

      <div class="tab-content">
        <div [id]="tile.tabId" role="tabpanel" class="tab-pane" [class.active]="activeTab === $index" *ngFor="let tile of tabbedView.tabs; trackBy let first = first; let $index = index">
            <ng-container *ngIf="tile && tile.content">
                <p [innerHTML]="tile.content"></p>
            </ng-container>
        </div>
        <ng-content>
        </ng-content>
      </div>
  </div>`, isInline: true, dependencies: [{ kind: "directive", type: i1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i1.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: TabbedViewComponent, decorators: [{
            type: Component,
            args: [{
                    selector: '[data-uxd-tabbed-view-cmp], [data-uxd-tabbed-view-v2-cmp]',
                    template: `
    <div class="tabs" [ngClass]="tabbedView.color">
      <ul class="nav nav-pills nav-justified" role='tablist'>
        <li  role="presentation"
            [class.active]="activeTab === $index"
            *ngFor="let tile of tabbedView.tabs; let first = first; let $index = index">
          <a (click)="tileClick($index, $event)"
            (keydown)="onKeyPress($index, $event)"
             href="javascript:void(0)"
            class="viewButton"
            [attr.aria-controls]="tile.tabId" role="tab"
            data-toggle="tab"
            [attr.aria-selected]="activeTab === $index"
            [attr.data-analytics]="getDataAnalytics(tile)"
            [innerHTML]="tile.heading" tabindex="0"></a>
        </li>
      </ul>

      <div class="tab-content">
        <div [id]="tile.tabId" role="tabpanel" class="tab-pane" [class.active]="activeTab === $index" *ngFor="let tile of tabbedView.tabs; trackBy let first = first; let $index = index">
            <ng-container *ngIf="tile && tile.content">
                <p [innerHTML]="tile.content"></p>
            </ng-container>
        </div>
        <ng-content>
        </ng-content>
      </div>
  </div>`
                }]
        }], propDecorators: { tabbedView: [{
                type: Input
            }], tabs: [{
                type: ContentChildren,
                args: [TabbedViewBodyComponent, { descendants: true }]
            }], activeTab: [{
                type: Input
            }] } });

class TabsModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: TabsModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "16.2.12", ngImport: i0, type: TabsModule, declarations: [AlphabetTabsBodyComponent,
            AlphabetTabsComponent,
            TabbedTileComponent,
            TabbedTilesComponent,
            TabbedViewBodyComponent,
            TabbedViewComponent,
            ScrollingTabbedViewBodyComponent,
            ScrollingTabbedViewComponent], imports: [CommonModule], exports: [AlphabetTabsBodyComponent,
            AlphabetTabsComponent,
            TabbedTileComponent,
            TabbedTilesComponent,
            TabbedViewBodyComponent,
            TabbedViewComponent,
            ScrollingTabbedViewBodyComponent,
            ScrollingTabbedViewComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: TabsModule, imports: [CommonModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: TabsModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule],
                    declarations: [
                        AlphabetTabsBodyComponent,
                        AlphabetTabsComponent,
                        TabbedTileComponent,
                        TabbedTilesComponent,
                        TabbedViewBodyComponent,
                        TabbedViewComponent,
                        ScrollingTabbedViewBodyComponent,
                        ScrollingTabbedViewComponent
                    ],
                    exports: [
                        AlphabetTabsBodyComponent,
                        AlphabetTabsComponent,
                        TabbedTileComponent,
                        TabbedTilesComponent,
                        TabbedViewBodyComponent,
                        TabbedViewComponent,
                        ScrollingTabbedViewBodyComponent,
                        ScrollingTabbedViewComponent
                    ]
                }]
        }] });

class AntTabLabel extends CdkPortal {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AntTabLabel, deps: null, target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "16.2.12", type: AntTabLabel, selector: "[ant-tab-label], [antTabLabel]", usesInheritance: true, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AntTabLabel, decorators: [{
            type: Directive,
            args: [{
                    selector: '[ant-tab-label], [antTabLabel]'
                }]
        }] });

class AntTabContent {
    constructor(template) {
        this.template = template;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AntTabContent, deps: [{ token: i0.TemplateRef }], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "16.2.12", type: AntTabContent, selector: "[ant-tab-content]", ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AntTabContent, decorators: [{
            type: Directive,
            args: [{ selector: '[ant-tab-content]' }]
        }], ctorParameters: function () { return [{ type: i0.TemplateRef }]; } });
class AntTabComponent {
    get content() {
        return this._contentPortal;
    }
    constructor(_viewContainerRef) {
        this._viewContainerRef = _viewContainerRef;
        this.textLabel = '';
        this.ariaLabel = '';
        this._stateChanges = new Subject();
        this._contentPortal = null;
    }
    ngOnInit() {
        this._contentPortal = new TemplatePortal(this._explicitContent, this._viewContainerRef);
    }
    ngOnChanges(changes) {
        if (this._explicitContent) {
            this._contentPortal = new TemplatePortal(this._explicitContent, this._viewContainerRef);
        }
        if (changes.hasOwnProperty('textLabel')) {
            this._stateChanges.next();
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AntTabComponent, deps: [{ token: i0.ViewContainerRef }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.2.12", type: AntTabComponent, selector: "[ant-tab]", inputs: { disabled: "disabled", textLabel: ["label", "textLabel"], ariaLabel: ["aria-label", "ariaLabel"], analytics: "analytics", ariaLabelledby: ["aria-labelledby", "ariaLabelledby"] }, queries: [{ propertyName: "templateLabel", first: true, predicate: AntTabLabel, descendants: true }, { propertyName: "_explicitContent", first: true, predicate: AntTabContent, descendants: true, read: TemplateRef, static: true }], viewQueries: [{ propertyName: "_implicitContent", first: true, predicate: TemplateRef, descendants: true, read: TemplateRef }], usesOnChanges: true, ngImport: i0, template: `<ng-template><ng-content></ng-content></ng-template>`, isInline: true }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AntTabComponent, decorators: [{
            type: Component,
            args: [{
                    selector: '[ant-tab]',
                    template: `<ng-template><ng-content></ng-content></ng-template>`
                }]
        }], ctorParameters: function () { return [{ type: i0.ViewContainerRef }]; }, propDecorators: { disabled: [{
                type: Input
            }], textLabel: [{
                type: Input,
                args: ['label']
            }], ariaLabel: [{
                type: Input,
                args: ['aria-label']
            }], analytics: [{
                type: Input,
                args: ['analytics']
            }], ariaLabelledby: [{
                type: Input,
                args: ['aria-labelledby']
            }], templateLabel: [{
                type: ContentChild,
                args: [AntTabLabel]
            }], _explicitContent: [{
                type: ContentChild,
                args: [AntTabContent, { read: TemplateRef, static: true }]
            }], _implicitContent: [{
                type: ViewChild,
                args: [TemplateRef, { read: TemplateRef }]
            }] } });

class AntTabBodyHost extends CdkPortalOutlet {
    constructor(componentFactoryResolver, viewContainerRef, _host) {
        super(componentFactoryResolver, viewContainerRef);
        this._host = _host;
    }
    ngOnInit() {
        super.ngOnInit();
        if (!this.hasAttached()) {
            this.attach(this._host._content);
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AntTabBodyHost, deps: [{ token: i0.ComponentFactoryResolver }, { token: i0.ViewContainerRef }, { token: forwardRef(() => AntTabBodyComponent) }], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "16.2.12", type: AntTabBodyHost, selector: "[antTabBodyHost]", inputs: { _content: ["content", "_content"] }, usesInheritance: true, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AntTabBodyHost, decorators: [{
            type: Directive,
            args: [{
                    selector: '[antTabBodyHost]'
                }]
        }], ctorParameters: function () { return [{ type: i0.ComponentFactoryResolver }, { type: i0.ViewContainerRef }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [forwardRef(() => AntTabBodyComponent)]
                }] }]; }, propDecorators: { _content: [{
                type: Input,
                args: ['content']
            }] } });
class AntTabBodyComponent {
    constructor(_changeDetectorRef) {
        this._changeDetectorRef = _changeDetectorRef;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AntTabBodyComponent, deps: [{ token: i0.ChangeDetectorRef }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.2.12", type: AntTabBodyComponent, selector: "ant-tab-body, [ant-tab-body]", inputs: { _content: ["content", "_content"] }, ngImport: i0, template: `
    <div class="ant-tab-body" #content>
      <ng-template antTabBodyHost></ng-template>
    </div>
  `, isInline: true, dependencies: [{ kind: "directive", type: AntTabBodyHost, selector: "[antTabBodyHost]", inputs: ["content"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush, encapsulation: i0.ViewEncapsulation.None }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AntTabBodyComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'ant-tab-body, [ant-tab-body]',
                    template: `
    <div class="ant-tab-body" #content>
      <ng-template antTabBodyHost></ng-template>
    </div>
  `,
                    changeDetection: ChangeDetectionStrategy.OnPush,
                    encapsulation: ViewEncapsulation.None
                }]
        }], ctorParameters: function () { return [{ type: i0.ChangeDetectorRef }]; }, propDecorators: { _content: [{
                type: Input,
                args: ['content']
            }] } });

let groupId = 0;
class AntTabChangeEvent {
}
class AntTabGroupHeaderComponent {
    /** The index of the active tab. */
    get selectedIndex() {
        return this._selectedIndex;
    }
    set selectedIndex(value) {
        if (this._selectedIndex !== value) {
            this._selectedIndex = value;
            if (this._keyboardManager) {
                this._keyboardManager.setSelectedItem(value);
            }
        }
    }
    /** Tracks which element has focus; used for keyboard navigation */
    get focusIndex() {
        return this._keyboardManager ? this._keyboardManager.selectedItemIndex : 0;
    }
    /** When the focus index is set, we must manually send focus to the correct label */
    set focusIndex(value) {
        if (!this._isValidIndex(value) || this.focusIndex === value || !this._keyboardManager) {
            return;
        }
        this._keyboardManager.setSelectedItem(value);
    }
    // tslint:disable-next-line: no-empty
    _itemSelected(event) { }
    // tslint:disable-next-line: no-empty
    constructor() {
        /** Event emitted when the option is selected. */
        this.selectFocusedIndex = new EventEmitter();
        /** Event emitted whenever a tab button is focused. */
        this.indexFocused = new EventEmitter();
        /** Event that emits when the header is destroyed (ngOnDestroy) */
        this._destroyed = new Subject();
        this._selectedIndex = 0;
    }
    ngAfterContentInit() {
        this._keyboardManager = new ListKeyManager(this._tabs);
        this._keyboardManager.setSelectedItem(0);
        this._keyboardManager.change.pipe(takeUntil(this._destroyed)).subscribe((tab) => {
            this._focusTab(tab); // focus the tab element
            this.indexFocused.emit(this.focusIndex);
        });
    }
    ngOnDestroy() {
        this._destroyed.next();
        this._destroyed.complete();
    }
    /** Handles keyboard events on the header. */
    _handleKeydown(event) {
        switch (event.keyCode) {
            case HOME:
                this._keyboardManager.selectFirstItem();
                event.preventDefault();
                break;
            case END:
                this._keyboardManager.selectLastItem();
                event.preventDefault();
                break;
            case ENTER:
            case SPACE:
                this.selectFocusedIndex.emit(this.focusIndex);
                this._itemSelected(event);
                break;
            default:
                this._keyboardManager.handleKeydown(event);
        }
    }
    /**
     * Determines if a specified index is valid, i.e. that a tab
     * exists in the array at that index.
     */
    _isValidIndex(index) {
        if (!this._tabs) {
            return false;
        }
        const tab = this._tabs ? this._tabs.toArray()[index] : null;
        return !!tab && !tab.isDisabled;
    }
    /**
     * Applies focus to the tab button by calling the
     * browser's default focus() function.
     */
    _focusTab(tab) {
        if (tab && tab.nativeElement) {
            tab.nativeElement.focus();
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AntTabGroupHeaderComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.2.12", type: AntTabGroupHeaderComponent, selector: "[ant-tab-group-header]", outputs: { selectFocusedIndex: "selectFocusedIndex", indexFocused: "indexFocused" }, host: { classAttribute: "ant-tab-header" }, queries: [{ propertyName: "_tabs", predicate: ["tabButton"] }], ngImport: i0, template: `
    <div
      class="ant-tab-list"
      role="tablist"
      (keydown)="_handleKeydown($event)">
          <ng-content></ng-content>
    </div>
  `, isInline: true }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AntTabGroupHeaderComponent, decorators: [{
            type: Component,
            args: [{
                    selector: '[ant-tab-group-header]',
                    host: {
                        class: 'ant-tab-header'
                    },
                    template: `
    <div
      class="ant-tab-list"
      role="tablist"
      (keydown)="_handleKeydown($event)">
          <ng-content></ng-content>
    </div>
  `
                }]
        }], ctorParameters: function () { return []; }, propDecorators: { _tabs: [{
                type: ContentChildren,
                args: ['tabButton']
            }], selectFocusedIndex: [{
                type: Output
            }], indexFocused: [{
                type: Output
            }] } });
/**
 * TabGroupComponent
 *
 */
class AntTabGroupComponent {
    /** The index of the active tab. */
    get selectedIndex() {
        return this._selectedIndex;
    }
    set selectedIndex(value) {
        if (this._indexToSelect !== value) {
            this._indexToSelect = value;
            const selectedTab = this._getTabByIndex(this._indexToSelect);
            this.onTabChange.emit(selectedTab);
        }
    }
    // tslint:disable-next-line: no-empty
    constructor(_changeDetectorRef) {
        this._changeDetectorRef = _changeDetectorRef;
        this._groupId = groupId++;
        /** The tab index that should be selected after click */
        this._indexToSelect = 0;
        /** Event emitted whenever the tab option changes */
        this.onTabChange = new EventEmitter();
        /** Event emitted when focus has changed within a tab group. */
        this.focusChange = new EventEmitter();
        this._selectedIndex = null;
    }
    // tslint:disable-next-line: no-empty
    ngOnInit() { }
    ngAfterContentChecked() {
        const indexToSelect = (this._indexToSelect = clampTabIndex(this._tabs.length, this._indexToSelect));
        if (this._selectedIndex !== indexToSelect) {
            this._selectedIndex = indexToSelect;
            this._changeDetectorRef.markForCheck();
        }
    }
    /** Returns a unique id for each tab label element */
    _getTabLabelId(i) {
        return `ant-tab-label-${this._groupId}-${i}`;
    }
    /** Retrieves the tabindex for the tab. */
    _getTabIndex(tab, idx) {
        if (tab.disabled) {
            return null;
        }
        return this.selectedIndex === idx ? 0 : -1;
    }
    /** Returns a unique id for each tab content element */
    _getTabBodyId(i) {
        return `ant-tab-body-${this._groupId}-${i}`;
    }
    _getTabByIndex(index) {
        if (this._tabs && this._tabs.length) {
            return this._tabs.toArray()[index];
        }
        return null;
    }
    /** Handle click events, setting new selected index if appropriate. */
    _handleClick(tab, index) {
        if (!tab.disabled) {
            this.selectedIndex = index;
        }
    }
    /** On focus handler -
     * Sets the focused index for application of focus styles */
    _focusChanged(index) {
        this._focusedIndex = index;
        this.focusChange.emit(this._createChangeEvent(index));
    }
    _createChangeEvent(index) {
        const event = new AntTabChangeEvent();
        event.index = index;
        event.tab = this._getTabByIndex(index);
        return event;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AntTabGroupComponent, deps: [{ token: i0.ChangeDetectorRef }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.2.12", type: AntTabGroupComponent, selector: "ant-tab-group", inputs: { selectedIndex: "selectedIndex" }, outputs: { onTabChange: "onTabChange", focusChange: "focusChange" }, host: { classAttribute: "ant-tab-group" }, queries: [{ propertyName: "_tabs", predicate: AntTabComponent }], ngImport: i0, template: `
    <div ant-tab-group-header
      (indexFocused)="_focusChanged($event)"
      (selectFocusIndex)="selectedIndex = $event">
      <button #tabButton class="ant-tab-button" role="tab"
      cdkMonitorElementFocus
      *ngFor="let tab of _tabs; let i = index"
        [id]="_getTabLabelId(i)"
        [attr.tabIndex]="_getTabIndex(tab, i)"
        [attr.aria-setsize]="_tabs.length"
        [attr.aria-controls]="_getTabBodyId(i)"
        [attr.aria-selected]="selectedIndex == i"
        [attr.aria-label]="tab.ariaLabel || null"
        [attr.data-analytics]="tab.analytics || null"
        [attr.aria-labelledby]="(!tab.ariaLabel && tab.ariaLabelledby) ? tab.ariaLabelledby : null"
        [class.is-active]="selectedIndex == i"
        [class.has-focus]="_focusedIndex == i"
        (click)="_handleClick(tab, i)">
          <div class="ant-tab-label">
            <ng-template [ngIf]="tab.templateLabel">
              <ng-template [cdkPortalOutlet]="tab.templateLabel"></ng-template>
            </ng-template>
            <ng-template [ngIf]="!tab.templateLabel">{{tab.textLabel}}</ng-template>
          </div>
        </button>
    </div>
    <ant-tab-body role="tabpanel"
    class="tab-body"
     *ngFor="let tab of _tabs; let i = index"
      [attr.aria-labelledby]="_getTabLabelId(i)"
      [id]="_getTabBodyId(i)"
      [content]="tab.content"
      [class.is-active]="selectedIndex == i">
    </ant-tab-body>
  `, isInline: true, dependencies: [{ kind: "directive", type: i1.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i2.CdkPortalOutlet, selector: "[cdkPortalOutlet]", inputs: ["cdkPortalOutlet"], outputs: ["attached"], exportAs: ["cdkPortalOutlet"] }, { kind: "directive", type: i3.CdkMonitorFocus, selector: "[cdkMonitorElementFocus], [cdkMonitorSubtreeFocus]", outputs: ["cdkFocusChange"], exportAs: ["cdkMonitorFocus"] }, { kind: "component", type: AntTabGroupHeaderComponent, selector: "[ant-tab-group-header]", outputs: ["selectFocusedIndex", "indexFocused"] }, { kind: "component", type: AntTabBodyComponent, selector: "ant-tab-body, [ant-tab-body]", inputs: ["content"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AntTabGroupComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'ant-tab-group',
                    host: {
                        class: 'ant-tab-group'
                    },
                    template: `
    <div ant-tab-group-header
      (indexFocused)="_focusChanged($event)"
      (selectFocusIndex)="selectedIndex = $event">
      <button #tabButton class="ant-tab-button" role="tab"
      cdkMonitorElementFocus
      *ngFor="let tab of _tabs; let i = index"
        [id]="_getTabLabelId(i)"
        [attr.tabIndex]="_getTabIndex(tab, i)"
        [attr.aria-setsize]="_tabs.length"
        [attr.aria-controls]="_getTabBodyId(i)"
        [attr.aria-selected]="selectedIndex == i"
        [attr.aria-label]="tab.ariaLabel || null"
        [attr.data-analytics]="tab.analytics || null"
        [attr.aria-labelledby]="(!tab.ariaLabel && tab.ariaLabelledby) ? tab.ariaLabelledby : null"
        [class.is-active]="selectedIndex == i"
        [class.has-focus]="_focusedIndex == i"
        (click)="_handleClick(tab, i)">
          <div class="ant-tab-label">
            <ng-template [ngIf]="tab.templateLabel">
              <ng-template [cdkPortalOutlet]="tab.templateLabel"></ng-template>
            </ng-template>
            <ng-template [ngIf]="!tab.templateLabel">{{tab.textLabel}}</ng-template>
          </div>
        </button>
    </div>
    <ant-tab-body role="tabpanel"
    class="tab-body"
     *ngFor="let tab of _tabs; let i = index"
      [attr.aria-labelledby]="_getTabLabelId(i)"
      [id]="_getTabBodyId(i)"
      [content]="tab.content"
      [class.is-active]="selectedIndex == i">
    </ant-tab-body>
  `
                }]
        }], ctorParameters: function () { return [{ type: i0.ChangeDetectorRef }]; }, propDecorators: { onTabChange: [{
                type: Output
            }], focusChange: [{
                type: Output
            }], selectedIndex: [{
                type: Input
            }], _tabs: [{
                type: ContentChildren,
                args: [AntTabComponent]
            }] } });
function clampTabIndex(length, index) {
    return Math.min(length - 1, Math.max(index || 0, 0));
}

/**
 * Tab Reference
 */
class TabRef {
    constructor(_nid = -1) {
        this._nid = _nid;
        this._isActive = false;
    }
    get isActive() {
        return this._isActive;
    }
    get nid() {
        return this._nid;
    }
    set nid(id) {
        this._nid = id;
    }
}
/**
 *
 */
class TabChangeEvent {
    constructor(tab, isUserInput = false) {
        this.tab = tab;
        this.isUserInput = isUserInput;
    }
}

let uuid = 0;
class AntTabNavBarComponent {
    get tabLinks() {
        return this._tabLinks.toArray();
    }
    constructor(_ngZone, _changeDetector) {
        this._ngZone = _ngZone;
        this._changeDetector = _changeDetector;
        this._activeTabChanges = defer(() => {
            if (this._tabLinks) {
                return merge(...this._tabLinks.map((tab) => tab.onActivationChange));
            }
            return this._ngZone.onStable.asObservable().pipe(take(1), switchMap(() => this._activeTabChanges));
        });
    }
    ngAfterContentChecked() {
        if (this._activeLinkHasChanged) {
            const activeTab = this._tabLinks.find((tab) => tab.isActive);
            this._activeLinkHasChanged = false;
        }
    }
    setActiveTab(tabElement) {
        this._activeLinkHasChanged = !!tabElement;
        this._changeDetector.markForCheck();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AntTabNavBarComponent, deps: [{ token: i0.NgZone }, { token: i0.ChangeDetectorRef }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.2.12", type: AntTabNavBarComponent, selector: "ant-tab-nav-bar", queries: [{ propertyName: "_tabLinks", predicate: i0.forwardRef(function () { return AntTabNavLinkDirective; }), descendants: true }], ngImport: i0, template: "<header class=\"tab-nav-bar\">\n  <nav class=\"tab-nav-bar__links\" role=\"tablist\">\n    <ng-content></ng-content>\n  </nav>\n</header>\n", encapsulation: i0.ViewEncapsulation.None }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AntTabNavBarComponent, decorators: [{
            type: Component,
            args: [{ selector: 'ant-tab-nav-bar', encapsulation: ViewEncapsulation.None, template: "<header class=\"tab-nav-bar\">\n  <nav class=\"tab-nav-bar__links\" role=\"tablist\">\n    <ng-content></ng-content>\n  </nav>\n</header>\n" }]
        }], ctorParameters: function () { return [{ type: i0.NgZone }, { type: i0.ChangeDetectorRef }]; }, propDecorators: { _tabLinks: [{
                type: ContentChildren,
                args: [forwardRef(() => AntTabNavLinkDirective), { descendants: true }]
            }] } });
class AntTabNavLinkDirective extends TabRef {
    constructor(hostElement, _parentNav) {
        super();
        this.hostElement = hostElement;
        this._parentNav = _parentNav;
        this._id = `tab-link-${uuid++}`;
        this.onActivationChange = new EventEmitter();
    }
    get active() {
        return this._isActive;
    }
    set active(value) {
        if (value !== this._isActive) {
            this._isActive = value;
            this._parentNav.setActiveTab(this.hostElement);
            this._emitActivationChange(false);
        }
    }
    get _tabIndex() {
        return this.isActive ? '0' : '-1';
    }
    get id() {
        return this._id;
    }
    // tslint:disable-next-line: no-empty
    ngOnInit() { }
    handleClick(event) {
        this.activate();
        event.preventDefault();
        this._emitActivationChange(true);
    }
    _emitActivationChange(isUserInput) {
        this.onActivationChange.emit(new TabChangeEvent(this, isUserInput));
    }
    activate() {
        this.active = true;
    }
    deactivate() {
        this.active = false;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AntTabNavLinkDirective, deps: [{ token: i0.ElementRef }, { token: AntTabNavBarComponent }], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "16.2.12", type: AntTabNavLinkDirective, selector: "[ant-tab-link]", inputs: { active: "active" }, outputs: { onActivationChange: "onActivationChange" }, host: { attributes: { "role": "tab" }, listeners: { "click": "handleClick($event)" }, properties: { "attr.aria-selected": "isActive", "attr.aria-current": "isActive", "attr.id": "id", "class.is-active": "isActive" }, classAttribute: "tab-link" }, usesInheritance: true, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: AntTabNavLinkDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[ant-tab-link]',
                    host: {
                        role: 'tab',
                        '[attr.aria-selected]': 'isActive',
                        '[attr.aria-current]': 'isActive',
                        '[attr.id]': 'id',
                        class: 'tab-link',
                        '[class.is-active]': 'isActive'
                    }
                }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }, { type: AntTabNavBarComponent }]; }, propDecorators: { active: [{
                type: Input
            }], onActivationChange: [{
                type: Output
            }], handleClick: [{
                type: HostListener,
                args: ['click', ['$event']]
            }] } });

class TabsModuleV2 {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: TabsModuleV2, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "16.2.12", ngImport: i0, type: TabsModuleV2, declarations: [AntTabNavBarComponent,
            AntTabNavLinkDirective,
            AntTabContent,
            AntTabGroupComponent,
            AntTabComponent,
            AntTabGroupHeaderComponent,
            AntTabBodyComponent,
            AntTabBodyHost,
            AntTabNavBarComponent,
            AntTabNavLinkDirective,
            AntTabComponent,
            AntTabContent,
            AntTabGroupComponent,
            AntTabLabel], imports: [CommonModule, PortalModule, A11yModule], exports: [AntTabNavBarComponent,
            AntTabNavLinkDirective,
            AntTabContent,
            AntTabGroupComponent,
            AntTabComponent,
            AntTabGroupHeaderComponent,
            AntTabBodyComponent,
            AntTabBodyHost,
            AntTabNavBarComponent,
            AntTabNavLinkDirective,
            AntTabComponent,
            AntTabContent,
            AntTabGroupComponent,
            AntTabLabel] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: TabsModuleV2, imports: [CommonModule, PortalModule, A11yModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: TabsModuleV2, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule, PortalModule, A11yModule],
                    declarations: [
                        AntTabNavBarComponent,
                        AntTabNavLinkDirective,
                        AntTabContent,
                        AntTabGroupComponent,
                        AntTabComponent,
                        AntTabGroupHeaderComponent,
                        AntTabBodyComponent,
                        AntTabBodyHost,
                        AntTabNavBarComponent,
                        AntTabNavLinkDirective,
                        AntTabComponent,
                        AntTabContent,
                        AntTabGroupComponent,
                        AntTabLabel
                    ],
                    exports: [
                        AntTabNavBarComponent,
                        AntTabNavLinkDirective,
                        AntTabContent,
                        AntTabGroupComponent,
                        AntTabComponent,
                        AntTabGroupHeaderComponent,
                        AntTabBodyComponent,
                        AntTabBodyHost,
                        AntTabNavBarComponent,
                        AntTabNavLinkDirective,
                        AntTabComponent,
                        AntTabContent,
                        AntTabGroupComponent,
                        AntTabLabel
                    ]
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { AlphabetTabsBodyComponent, AlphabetTabsComponent, AntTabBodyComponent, AntTabBodyHost, AntTabChangeEvent, AntTabComponent, AntTabContent, AntTabGroupComponent, AntTabGroupHeaderComponent, AntTabLabel, AntTabNavBarComponent, AntTabNavLinkDirective, ScrollingTabbedViewBodyComponent, ScrollingTabbedViewComponent, TabbedTileComponent, TabbedTilesComponent, TabbedViewBodyComponent, TabbedViewComponent, TabsModule, TabsModuleV2 };
